import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { CollapsibleFilters, FilterField, FilterToggleButton } from '@/components/common/CollapsibleFilters';
import { TableHeaderControls } from '@/components/common/TableHeaderControls';
import { ColumnVisibility } from '@/components/common/ColumnVisibility';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useColumnVisibility } from '@/lib/hooks/useColumnVisibility';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/common/Pagination';
import { useDebounce } from '@/lib/hooks/useDebounce';
import type { Contract, ContractFilters } from '../types';

interface ContractsDataTableProps<TData extends Contract> {
  data: TData[];
  columns: ColumnDef<TData>[];
  isLoading?: boolean;
  filters: ContractFilters;
  updateFilter: <K extends keyof ContractFilters>(key: K, value: ContractFilters[K]) => void;
  updateFilters?: (updates: Partial<ContractFilters>) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  totalCount?: number;
  selectedFields?: {
    client?: { id?: number; disabled?: boolean };
  };
  actionButton?: React.ReactNode;
  onClearFilters?: () => void;
  /** Page-header slot for the Columns/Filters buttons (TableHeaderControls). */
  controlsContainer?: HTMLElement | null;
}

export function ContractsDataTable<TData extends Contract>({
  data,
  columns,
  isLoading,
  filters,
  updateFilter,
  updateFilters,
  onPageChange,
  onLimitChange,
  totalCount = 0,
  selectedFields,
  actionButton,
  onClearFilters,
  controlsContainer,
}: ContractsDataTableProps<TData>) {
  const { t } = useTranslation();
  const [columnVisibility, setColumnVisibility] = useColumnVisibility('contracts');
  const tableZoom = useTableZoom();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [rowSelection, setRowSelection] = React.useState({});

  // Debounced search
  const [searchValue, setSearchValue] = React.useState(filters.s ?? '');
  const debouncedSearch = useDebounce(searchValue, 300);
  const isFirstRender = React.useRef(true);

  React.useEffect(() => {
    // Skip first render to avoid resetting page on mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const trimmed = debouncedSearch.trim();
    const newValue = trimmed.length >= 2 ? trimmed : undefined;

    // Only update if value actually changed to avoid resetting page
    if (newValue !== filters.s) {
      updateFilter('s', newValue);
    }
  }, [debouncedSearch]);

  // Debounced exact-id filter (notification deep-links land as `?id=`).
  const [idValue, setIdValue] = React.useState(filters.id?.toString() ?? '');
  const debouncedId = useDebounce(idValue, 300);
  const isFirstIdRender = React.useRef(true);

  React.useEffect(() => {
    if (isFirstIdRender.current) {
      isFirstIdRender.current = false;
      return;
    }
    const num = Number.parseInt(debouncedId.trim(), 10);
    const newValue = Number.isNaN(num) || num <= 0 ? undefined : num;
    if (newValue !== filters.id) {
      updateFilter('id', newValue);
    }
  }, [debouncedId]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnVisibility,
      rowSelection,
    },
  });

  const currentPage = filters.page ?? 0;
  const limit = filters.limit ?? 50;

  // Count active filters
  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (filters.id) count++;
    if (filters.type) count++;
    if (filters.practiceAreaId) count++;
    if (filters.clientId) count++;
    if (filters.s) count++;
    return count;
  }, [filters]);

  const handleClearFilters = () => {
    setSearchValue('');
    setIdValue('');
    onClearFilters?.();
  };

  return (
    <div className="w-full space-y-4">
      {/* Status Toggle */}
      <ToggleGroup
        type="single"
        size="sm"
        className="justify-start gap-1"
        value={filters.status ?? 'none'}
        onValueChange={(val) => updateFilter('status', val === 'none' ? undefined : val as ContractFilters['status'])}
      >
        <ToggleGroupItem
          value="none"
          aria-label="All"
          className="rounded-full border px-4 data-[state=on]:bg-primary data-[state=on]:text-white"
        >
          {t('all')}
        </ToggleGroupItem>
        <ToggleGroupItem
          value="NEW"
          aria-label="New"
          className="rounded-full border px-4 data-[state=on]:bg-blue-500 data-[state=on]:text-white"
        >
          {t('new')}
        </ToggleGroupItem>
        <ToggleGroupItem
          value="ACTIVE"
          aria-label="Active"
          className="rounded-full border px-4 data-[state=on]:bg-emerald-500 data-[state=on]:text-white"
        >
          {t('active')}
        </ToggleGroupItem>
        <ToggleGroupItem
          value="CLOSED"
          aria-label="Closed"
          className="rounded-full border px-4 data-[state=on]:bg-gray-500 data-[state=on]:text-white"
        >
          {t('closed')}
        </ToggleGroupItem>
        <ToggleGroupItem
          value="TERMINATED"
          aria-label="Terminated"
          className="rounded-full border px-4 data-[state=on]:bg-red-500 data-[state=on]:text-white"
        >
          {t('terminated')}
        </ToggleGroupItem>
      </ToggleGroup>

      {/* Table Actions Bar */}
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-muted-foreground">
          {totalCount > 0 && (
            <span>{t('showing')} {Math.min((currentPage * limit) + 1, totalCount)}-{Math.min((currentPage + 1) * limit, totalCount)} {t('of')} {totalCount}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <TableHeaderControls container={controlsContainer}>
            <ColumnVisibility table={table} />
            <FilterToggleButton
              activeFilterCount={activeFilterCount}
              open={filtersOpen}
              onToggle={() => setFiltersOpen((v) => !v)}
            />
          </TableHeaderControls>
          {actionButton}
        </div>
      </div>

      {/* Filters panel */}
      <CollapsibleFilters
        open={filtersOpen}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearFilters}
        gridCols={4}
      >
        {/* Type Filter */}
        <FilterField label={t('type')}>
          <Select
            value={filters.type ?? 'none'}
            onValueChange={(val) => updateFilter('type', val === 'none' ? undefined : val as ContractFilters['type'])}
          >
            <SelectTrigger className="w-full h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]">
              <SelectValue placeholder={t('all')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t('all')}</SelectItem>
              <SelectItem value="HOURLY">{t('hourly')}</SelectItem>
              <SelectItem value="SUCCESS_FEE">{t('successFee')}</SelectItem>
              <SelectItem value="FIXED">{t('fixed')}</SelectItem>
              <SelectItem value="PRO_BONO">{t('proBono')}</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>

        {/* Practice Area Filter */}
        <FilterField label={t('practiceArea')}>
          <UniversalCombobox
            url="/contract-practice-areas"
            labelKey="name"
            valueKey="id"
            selectedId={filters.practiceAreaId}
            onSelectItem={(item) => updateFilter('practiceAreaId', item?.['id'] as number | undefined)}
            placeholder={t('all')}
            className="w-full"
          />
        </FilterField>

        {/* Client Filter */}
        <FilterField label={t('client')}>
          <UniversalCombobox
            url="/clients"
            labelKey="name"
            labelKeys={['lastname', 'name', 'middlename']}
            valueKey="id"
            onSelectItem={(item) => updateFilter('clientId', item?.['id'] as number | undefined)}
            placeholder={t('all')}
            selectedId={selectedFields?.client?.id ?? filters.clientId}
            disabled={selectedFields?.client?.disabled ?? false}
            className="w-full"
          />
        </FilterField>

        {/* Search */}
        <FilterField label={t('search')}>
          <div className="relative">
            <Input
              placeholder={t('search')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pr-10 h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
            />
            <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </FilterField>

        {/* Exact contract ID */}
        <FilterField label={t('contractId')}>
          <Input
            type="number"
            placeholder={t('id')}
            value={idValue}
            onChange={(e) => setIdValue(e.target.value)}
            className="h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
          />
        </FilterField>
      </CollapsibleFilters>

      {/* Table */}
      <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
        <Table style={{ zoom: tableZoom.zoom }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} className="hover:bg-[#f8fafc]">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="py-8">
                    <h3 className="mb-2 text-xl font-medium text-[#1a2b4a]">{t('noDataAvailable')}</h3>
                    <p className="text-[#64748b]">{t('trackDataToBetterManage')}</p>
                    {actionButton && <div className="mt-4">{actionButton}</div>}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <Pagination
        leadingControls={<TableZoomControls zoom={tableZoom} />}
        page={currentPage}
        limit={limit}
        total={totalCount}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
        isLoading={isLoading}
      />
    </div>
  );
}
